/* eslint-disable */
// @ts-nocheck
import moment from 'moment-timezone';
import * as R from 'ramda';

/* -------------------------------------------------------------------------
To use a date utility method or constant in a component, see
all PUBLIC EXPORTS listed at the BOTTOM of this document.
------------------------------------------------------------------------- */
import type { Range, CalendarFormat } from './dateUtilHelpers';
import {
  _convertFormat,
  _prefixWithYear,
  _convertToTimezone,
  _getFirstMoment,
  _getLastMoment,
  _getRangeInTimezone,
  _applyTimezoneOffsetToTickValues,
  _addFirstOrLastTick,
  _getTimezoneListWithOffsets,
  _sortTimezonesByOffset,
  _getBestGuessInList,
  _getMomentFromOffset,
  _getCollectionWindow,
  _getLabelForTimezoneName,
  _determineDefaultMinimumDate,
  _convertToCalendarTimezone,
  _isMomentBridged,
  _getTimeZoneAbbr,
} from './dateUtilHelpers';
import { DateRangeInfo } from '../types/dateRangeTypes';

/* -------------------------------------------------------------------------
CONSTANTS - Special dates
------------------------------------------------------------------------- */
// the first date in Druid where we store data
// used in determining the approriate minimumDate in the DateRange component
const FIRST_DRUID_DATE = '2018-08-01';

/* -------------------------------------------------------------------------
CONSTANTS - FORMAT definitions
------------------------------------------------------------------------- */

/*
 * default for how the dashboard stores dates internally (i.e. when the program executes, not what is necessarily displayed to the user).
 *
 * '2018-05-09'
 */
const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

/*
 * how the date is presented to the user in the timeseries and the DateRange component.
 *
 * '05/09'
 */
const TIMESERIES_DATE_FORMAT = 'MM/DD';

/*
 * how the date is presented ot the user in the timeseries and the DateRange component for 'month' granularity
 *
 * '2020/01'
 */
const TIMESERIES_MONTH_FORMAT = 'YYYY/MM';

/*
 * how the date is presented to the user in the Custom Exports Created table of the Custom Exports UI.
 *
 * '2019 Jun 19'
 */
const CUSTOM_EXPORTS_DATE_FORMAT = 'YYYY MMM D';

/*
 * how the time is presented to the user in the Custom Exports Created table of the Custom Exports UI.
 *
 * '1:49 pm'
 */
const CUSTOM_EXPORTS_TIME_FORMAT = 'h:mm a';

/*
 * timestamp formatted with no offset. Rarely used.
 *
 * '2018-05-09T12:30:00'
 */
const NO_OFFSET_TIMESTAMP_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/*
 * Timestamp formatted with the date and 12-hour clock time. currently only used in liveview screen.
 *
 * '2018-05-09 12:30:00 PM'
 */
const DATE_WITH_TWELVE_HOUR_TIMESTAMP_FORMAT = `${DEFAULT_DATE_FORMAT} h:mm:ss A`;

/*
 * How the time and date are presented in the Recent Activity Tab.
 */
const RECENT_ACTIVITY_DATE_FORMAT = 'YYYY MMM D - h:mm A';

/*
 * How the dates are presented in the MAU Progress Bar in the Billing Section.
 */
const BILLING_DATE_FORMAT = 'MMM DD, YYYY';

/*
 * This WITH_OFFSET_TIMESTAMP_FORMAT is the same as moment library's default,
 * we do NOT therefore need to use whenever we use .format() method.
 * You likely won't need to import this format and instead should be
 * importing a utility function below.
 *
 * '2018-05-09T12:30:00-07:00'
 */
const WITH_OFFSET_TIMESTAMP_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
const WITH_OFFSET_TIMESTAMP_FORMAT_NO_T = 'YYYY-MM-DD HH:mm:ss';
const UNIX_SECONDS_FORMAT = 'X';
const UNIX_MILLISECONDS_FORMAT = 'x';
const RANGE_DATE_FORMAT = 'YYYY/MM/DD';
const TIMEZONE_FORMAT = 'z';

/* -------------------------------------------------------------------------
INITIALIZATION - used to determine and set the app's timezone
------------------------------------------------------------------------- */
// This is the default used for running unit tests (we always want
// the unit tests to run the same regardless of where the developer is)
let appTimezone = 'America/Los_Angeles';
let guess = appTimezone;
let timezoneIsGuessed = false;

// called on componentWillMount on MainApp
const loadTimezone = (timezoneInAppTable: string | null | undefined) => {
  // Here we set the timezone when the dashboard loads to the best guess based
  // on location if it's not set in the app table yet. Hard-coded default from
  // `appTimezone` definition is the last resort fallback.
  guess = _getBestGuessInList() || appTimezone;
  // set this variable to true if there is no timezone in app table
  timezoneIsGuessed = !timezoneInAppTable;
  appTimezone = timezoneInAppTable || guess; // Please note we cannot use moment.setDefault here - it globally sets
  // the default, which doesn't always place nicely with other libraries
  // (example: react-daterange-picker only works with timezone same as browser's)
};

/* -------------------------------------------------------------------------
PUBLIC method definitions:

These are essentially closures around the private methods defined in
dateUtilHelpers.js.

We want the public versions we use in other components to already have
the timezone of the app set, to save us from having to import the app's
timezone into each component in addition to the util fn needed.

Documentation for all methods are with the private versions in dateUtilHelpers.js
------------------------------------------------------------------------- */
const getValidDate = (timestamp: string): Date | void => {
  const date = new Date(timestamp);

  if (isNaN(date.getTime())) {
    return undefined;
  }

  return date;
};

const convertFormat = (timestamp: string, inputFormat: string, outputFormat: string) => {
  return _convertFormat(timestamp, inputFormat, outputFormat);
};

const convertDateToDefaultFormat = (date: any) => moment(date).format(DEFAULT_DATE_FORMAT);

const prefixWithYear = (timestamp: string): string => {
  return _prefixWithYear(timestamp, appTimezone);
};

const convertToTimezone = (timestamp: string | number | Date | moment, format?: string): string => {
  return _convertToTimezone(timestamp, appTimezone, format);
};

const convertToCalendarTimezone = (timestamp: string, format?: CalendarFormat) => {
  return _convertToCalendarTimezone(timestamp, appTimezone, format);
};

const getFirstMoment = (timestamp: string, format?: string): string => {
  return _getFirstMoment(timestamp, appTimezone, format);
};

const getMomentFromOffset = (offset: number, startDate: string, granularity: string) => {
  return _getMomentFromOffset(offset, startDate, granularity, appTimezone);
};

const getFirstMomentBrowser = (timestamp: string): string => {
  const formatted = _getFirstMoment(timestamp, guess);

  return moment.tz(formatted, guess);
};

const getLastMoment = (timestamp: string, format?: string): string => {
  return _getLastMoment(timestamp, appTimezone, format);
};

const getLastMomentBrowser = (timestamp: string): string => {
  const formatted = _getLastMoment(timestamp, guess);

  return moment.tz(formatted, guess);
};

const getRangeInTimezoneFromEndDate = (endDate: string, duration: number, granularity: string = 'days'): Range => {
  return _getRangeInTimezone(appTimezone, endDate, duration, granularity);
};

const getRangeInTimezoneEndingNow = (
  duration: number,
  format: string = WITH_OFFSET_TIMESTAMP_FORMAT,
  granularity: string = 'days'
): Range => {
  return _getRangeInTimezone(appTimezone, moment().format(), duration, granularity, format);
};

const createMomentObjInTimezone = (timestamp: string, timezone: string = appTimezone): moment => {
  return moment.tz(timestamp, timezone);
};

const applyTimezoneOffsetToTickValues = (tickValues: Array<Date>): Array<Date> => {
  const browserTimezone = moment.tz.guess();
  return _applyTimezoneOffsetToTickValues(tickValues, appTimezone, browserTimezone);
};

const addFirstOrLastTick = (tickValues: Array<Record<string, any>>): Array<Record<string, any>> => {
  return _addFirstOrLastTick(tickValues, appTimezone, moment.tz.guess());
};

const getTimezoneListWithOffsets = () => {
  return _sortTimezonesByOffset(_getTimezoneListWithOffsets());
};

const formatDateRange = (dateRange: Record<string, any> = {}, format: string) => {
  if (!dateRange.start_date || !dateRange.end_date) {
    return dateRange;
  }

  return {
    start_date: getFirstMoment(dateRange.start_date, format),
    end_date: getLastMoment(dateRange.end_date, format),
  };
};

/**
 * method that concatinates an extra tick value that represents 24 hours after the first (and only) tick value in tickValues.
 * NOTE: in order to center a data point when a user selects a single day on the timeseries chart, the xDomain must span the entire 24 hour period from the first to last moment of the day in question.
 */
const handleSingleDayDomain = (tickValues: Array<Record<string, any>>): Array<Record<string, any>> => {
  // need to create a new moment object for reat-vis's API
  return tickValues.concat(moment(tickValues[0].clone().add(1, 'days').format()));
};

const convertToUTC = (timestamp: string, format?: string): string => {
  return moment.tz(timestamp, 'Etc/UTC').format(format || NO_OFFSET_TIMESTAMP_FORMAT);
};

const getCurrentTimeInUTC = (format?: string): string => {
  return convertToUTC(moment(), format);
};

const isAfterToday = (momentObj: Record<string, any>) => {
  const today = moment();
  return today.diff(momentObj) < 0;
};

const isToday = (momentObj: Record<string, any>) => {
  const today = moment();
  return momentObj.format(DEFAULT_DATE_FORMAT) === moment.tz(today, appTimezone).format(DEFAULT_DATE_FORMAT);
};

const isYesterday = (momentObj: Record<string, any>) => {
  const yesterday = moment().add(-1, 'days');
  return momentObj.format(DEFAULT_DATE_FORMAT) === moment.tz(yesterday, appTimezone).format(DEFAULT_DATE_FORMAT);
};

/**
 * Method that returns whether date is contained chronologically between startDate and endDate.
 * startDate represents the first moment of that date (midnight) while endDate represents the last moment of that date (23:59:59).
 * If date and startDate are the same, this is considered as the date range occuring after the date and we therefore return false (not bridged).
 */
const isMomentBridged = (date: string, startDate: string, endDate: string) => {
  return _isMomentBridged(date, startDate, endDate, appTimezone);
};

/**
 * method that return the amount of time in hours it takes for cohort data to successfully appear on the dashboard
 */
const getCollectionWindow = (timestamp: string) => {
  return _getCollectionWindow(timestamp, appTimezone);
};

/**
 * method that returns whether a moment in time is the specified number of hours (hours) from today.
 * The moment in time is rounded up to the first moment in time of the following day.
 */
const isSpecifiedHoursFromToday = (momentObj: Record<string, any>, hours: number) => {
  const today = moment();
  const startOfNextDay = moment.tz(getFirstMoment(momentObj.add(1, 'days').format()), appTimezone);
  return startOfNextDay.add(hours, 'hours').format(UNIX_SECONDS_FORMAT) > today.format(UNIX_SECONDS_FORMAT);
};

const sortTimestampsChronologically = (timestamps: Array<string>) => {
  return R.sort((t1, t2) => {
    return (
      parseInt(_convertToTimezone(t1, appTimezone, UNIX_SECONDS_FORMAT)) -
      parseInt(_convertToTimezone(t2, appTimezone, UNIX_SECONDS_FORMAT))
    );
  }, timestamps);
};

const isFirstTimeStampLaterThanSecond = (firstTimestamp: string, secondTimestamp: string) => {
  const firstDayMidnightInTimezoneTimestamp = _getFirstMoment(firstTimestamp, appTimezone);

  const secondDayMidnightInTimezoneTimestamp = _getFirstMoment(secondTimestamp, appTimezone);

  const timestampsSorted = sortTimestampsChronologically([
    firstDayMidnightInTimezoneTimestamp,
    secondDayMidnightInTimezoneTimestamp,
  ]);
  return timestampsSorted[1] === firstDayMidnightInTimezoneTimestamp;
};

const getLabelForTimezoneName = (timezone: string = appTimezone) => {
  return _getLabelForTimezoneName(_getTimezoneListWithOffsets(), timezone);
};

// [EG-1165] minimum date determined by two_year_data_retention flipper value
// true => 24 months before now, false => 6 months before now
// returns FIRST_DRUID_DATE if the calculated minimum date is before then
const determineDefaultMinimumDate = (currentDate: Record<string, any>, two_year_data_retention: boolean) => {
  return _determineDefaultMinimumDate(currentDate, two_year_data_retention);
};

const subtractFromDate = (date: any, quantity: number, unit: 'days' | 'months' | 'years') =>
  moment(date).subtract(quantity, unit).toDate();

const addToDate = (date: any, quantity: number, unit: 'days' | 'months' | 'years') =>
  moment(date).add(quantity, unit).toDate();

// returns true if targetDate is before referenceDate
const isBefore = (targetDate: string, referenceDate: string) => moment(targetDate).diff(moment(referenceDate)) < 0;

const isValidDate = (date: string) => {
  return moment(date).isValid();
};

const getStartOfCurrentMonth = () => moment().startOf('month').format(DEFAULT_DATE_FORMAT);

const getStartOfPrevMonth = () => moment().startOf('month').add(-1, 'month').format(DEFAULT_DATE_FORMAT);

const getEndOfPrevMonth = () => moment().add(-1, 'month').endOf('month').format(DEFAULT_DATE_FORMAT);

const getStartOfCurrentWeek = () => moment().startOf('week').format(DEFAULT_DATE_FORMAT);

const timeToDate = (date: any, displayUnit: 'hours' | 'days' | 'months' | 'years'): number => {
  return moment(date).diff(new Date(), displayUnit);
};

const getTimeZoneAbbr = (): string => {
  return _getTimeZoneAbbr(appTimezone);
};

const handleGetDateRangeInfo = (dateRangeInfo: Partial<DateRangeInfo>, initialDefaultRange: number = 7): DateRangeInfo => {
  const { dateRange, defaultRange } = dateRangeInfo;

  if (dateRange) {
    return {
      dateRange,
      defaultRange: null,
    };
  }

  if (defaultRange) {
    const dateRange = getRangeInTimezoneEndingNow(defaultRange, DEFAULT_DATE_FORMAT);
    return {
      dateRange,
      defaultRange,
    };
  }

  const initDateRange = getRangeInTimezoneEndingNow(initialDefaultRange, DEFAULT_DATE_FORMAT);
  return {
    dateRange: initDateRange,
    defaultRange: initialDefaultRange,
  };
};

/* -------------------------------------------------------------------------
Public CONSTANT exports:

1. appTimezone and guess - you will RARELY need either of these in other files.
   If you expect you need the app's timezone ensure there isn't a method you
   could import (or create) and use instead.

2. Date formats - mostly the default format is what we want so you'll also
   rarely have to import these.

Documentation can be found with their definitions
at the top of the dateUtilHelpers.js page.
------------------------------------------------------------------------- */
export {
  appTimezone,
  guess,
  timezoneIsGuessed,
  FIRST_DRUID_DATE,
  DEFAULT_DATE_FORMAT,
  TIMESERIES_DATE_FORMAT,
  TIMESERIES_MONTH_FORMAT,
  CUSTOM_EXPORTS_DATE_FORMAT,
  CUSTOM_EXPORTS_TIME_FORMAT,
  NO_OFFSET_TIMESTAMP_FORMAT,
  DATE_WITH_TWELVE_HOUR_TIMESTAMP_FORMAT,
  WITH_OFFSET_TIMESTAMP_FORMAT,
  WITH_OFFSET_TIMESTAMP_FORMAT_NO_T,
  RANGE_DATE_FORMAT,
  UNIX_SECONDS_FORMAT,
  UNIX_MILLISECONDS_FORMAT,
  RECENT_ACTIVITY_DATE_FORMAT,
  BILLING_DATE_FORMAT,
  TIMEZONE_FORMAT,
};

/* -------------------------------------------------------------------------
Public METHOD exports

Documentation can be found with the corresponding private method definitions
on the dateUtilHelpers.js page.
------------------------------------------------------------------------- */
export {
  loadTimezone,
  convertFormat,
  convertDateToDefaultFormat,
  prefixWithYear,
  getValidDate,
  convertToTimezone,
  getFirstMoment,
  getFirstMomentBrowser,
  getLastMoment,
  getLastMomentBrowser,
  getRangeInTimezoneFromEndDate,
  getRangeInTimezoneEndingNow,
  createMomentObjInTimezone,
  applyTimezoneOffsetToTickValues,
  addFirstOrLastTick,
  getTimezoneListWithOffsets,
  convertToUTC,
  getCurrentTimeInUTC,
  handleSingleDayDomain,
  isToday,
  isYesterday,
  sortTimestampsChronologically,
  formatDateRange,
  isSpecifiedHoursFromToday,
  getMomentFromOffset,
  getCollectionWindow,
  isAfterToday,
  isFirstTimeStampLaterThanSecond,
  getLabelForTimezoneName,
  determineDefaultMinimumDate,
  convertToCalendarTimezone,
  isMomentBridged,
  subtractFromDate,
  addToDate,
  isBefore,
  isValidDate,
  getStartOfCurrentMonth,
  getStartOfPrevMonth,
  getEndOfPrevMonth,
  getStartOfCurrentWeek,
  timeToDate,
  getTimeZoneAbbr,
  handleGetDateRangeInfo,
};
